import React from 'react';
import { motion } from 'framer-motion';
import './css/BusinessVertical.css';
import SEO from '../components/SEO';
import { serviceSchema, productSchema } from '../components/StructuredData';

const HeatDissipation = () => {
  const structuredData = [
    serviceSchema(
      "Heat Dissipation Compounds & Thermal Management",
      "Advanced nano-based heat sink compounds and thermal pastes for improving heat transfer efficiency in electronics, LEDs, EVs, and power electronics.",
      "Thermal Management Solutions"
    ),
    productSchema(
      "Thermal Interface Materials",
      "High-performance thermal compounds for electronics cooling applications",
      "Heat Dissipation Solutions"
    )
  ];

  return (
    <>
      <SEO
        title="Heat Dissipation Compounds - Advanced Thermal Management | Nimble Group"
        description="Leading manufacturer of nano-based heat sink compounds and thermal pastes for electronics, LEDs, EVs, and power electronics. 10+ years expertise with 3.0 W/m·K thermal conductivity."
        keywords="Thermal Gel Manufacturers, Thermal paste manufacturer, Thermal compound supplier, Thermal grease wholesale, Heat sink compound manufacturer, Heat sink paste for electronics, Heat sink paste for PCB, Thermal gel suppliers, Thermal Interface Material (TIM) manufacturer, Thermally conductive material supplier, High thermal conductivity paste, Non-silicone thermal grease supplier, Silicone thermal compound bulk, Phase Change Material (PCM) manufacturer, Gap filler pad supplier, Thermal adhesive manufacturer, Thermal compound manufacturer for electronics, Thermal Paste for Electronics, Thermal management solutions, Heat dissipation material supplier industrial, Thermal paste for LED lighting manufacturers, Bulk thermal paste supplier, Custom thermal compound formulation, Thermal interface material for power electronics, RoHS compliant thermal paste manufacturer, Heat transfer compounds, Heat transfer paste, Heat transfer gel, High W/make thermal paste, Electrically insulating thermal compound, Low thermal resistance paste, Thermal paste with low oil bleed, Thermal paste manufacturers Mumbai, Heat Sink Paste Manufacturer, Heat Sink compound manufacturers, Thermal paste manufacturer India, Thermal paste manufacturer Mumbai, Thermal compound supplier India, Thermal compound supplier Mumbai, Thermal grease Manufacturers, Thermal Grease manufacturers Mumbai, Thermal Grease manufacturers India, Thermal grease suppliers, Heat sink compound manufacturer, Heat sink compound, Heat sink paste for electronics, Heat sink paste Manufacturers Mumbai, Thermal gel manufactures, Heat dissipation material, Heat dissipation compound, Heat dissipation compound manufacturers, Heat sink compound for PCB, Thermal Pate for PCB, heat dissipation compounds, thermal management, thermal pastes, heat sink compounds, electronics cooling, LED thermal management, EV thermal solutions"
        canonical="/business/heat-dissipation"
        structuredData={structuredData}
      />
      <HeatDissipationContent />
    </>
  );
};

const HeatDissipationContent = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  // Key stats with animation
  const stats = [
    { value: '10+', label: 'Years of Expertise' },
    { value: '3.0', label: 'W/m·K Thermal Conductivity' },
    { value: '300°C+', label: 'Temperature Stability' },
    { value: '100%', label: 'VOC-Free Solutions' }
  ];

  return (
    <div className="business-vertical-page">
      <motion.div 
        className="business-vertical-hero heat-dissipation"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <h1>Thermal Management</h1>
          <h2 className="tagline">"Advancing electronic efficiency with superior thermal management solutions."</h2>
        </div>
      </motion.div>

      <section className="business-vertical-content">
        <div className="container">
          <motion.div
            className="stats-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {stats.map((stat, index) => (
              <motion.div 
                className="stat-box"
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
              >
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={fadeIn.transition}
          >
            <h3>About HEATGO® Thermal Management Solutions</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🔥</span>
              </div>
              <div className="section-content">
                <p>HEATGO®, a dedicated Strategic Business Unit of our organization, specializes in the development and manufacture of advanced heat dissipation and thermal management compounds. Focused on delivering precise, efficient, and long-term thermal control solutions, this division supports critical applications across electronics, LED lighting, instrumentation, automotive electronics, and electrical appliances.</p>
                <p>Leveraging over a decade of formulation expertise, offers a complete portfolio of silicone-based and non-silicone-based thermal interface compounds (TICs) engineered to meet diverse thermal conductivity, dielectric, and stability demands.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.1 }}
          >
            <h3>Product Portfolio</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">📊</span>
              </div>
              <div className="section-content">
                <p>Our thermal management compounds under brand name HEATGO are formulated for efficient heat transfer and dissipation, improving system reliability and extending the operational life of heat-sensitive components. Available in multiple viscosity profiles and thermal conductivities ranging from 1.0 to 3.0 W/m·K, these compounds are suitable for a variety of assembly and packaging processes, including:</p>
                <ul>
                  <li>Screen Printing</li>
                  <li>Stamping</li>
                  <li>Manual Application</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.2 }}
          >
            <h3>Key Technical Features</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">⚙️</span>
              </div>
              <div className="section-content">
                <ul>
                  <li><strong>Superior Thermal Conductivity:</strong> Stable thermal conductivity performance over a wide operational temperature range.</li>
                  <li><strong>Exceptional High-Temperature Stability:</strong> Continuous performance even at sustained temperatures exceeding 300°C.</li>
                  <li><strong>Zero Bleed, Zero Creep:</strong> Maintains bond line integrity without oil separation, bleeding, or migration under thermal stress.</li>
                  <li><strong>Non-Flammable & VOC-Free:</strong> Safe for use in enclosed electronic and industrial environments.</li>
                  <li><strong>Outstanding Dielectric Properties:</strong> Ensures electrical insulation alongside thermal performance.</li>
                  <li><strong>Oxidation & Corrosion Resistant:</strong> Provides protective barrier preventing substrate degradation.</li>
                  <li><strong>Low Bond Line Thickness:</strong> Enables efficient heat transfer at minimal material consumption.</li>
                  <li><strong>Application Flexibility:</strong> Compatible with screen printing, stamping, dispensing, or hand application for diverse assembly processes.</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.3 }}
          >
            <h3>Application Sectors</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🔌</span>
              </div>
              <div className="section-content">
                <p>Our heat dissipation compounds are extensively used in:</p>
                <ul>
                  <li>Electronic Devices & PCB Assemblies</li>
                  <li>LED Lighting Modules</li>
                  <li>Electrical Appliances & Control Panels</li>
                  <li>Measurement Instruments & Medical Electronics</li>
                  <li>Power Modules & Automotive Electronics</li>
                  <li>Industrial Heat Transfer Applications</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.4 }}
          >
            <h3>Material Variants</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🧪</span>
              </div>
              <div className="section-content">
                <ul>
                  <li><strong>Silicone-Based Thermal Compounds:</strong> High-performance, long-term stability, and wide temperature tolerance.</li>
                  <li><strong>Non-Silicone-Based Thermal Compounds:</strong> Designed for silicone-sensitive applications with equivalent thermal and dielectric characteristics.</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.5 }}
          >
            <h3>Commitment to Performance & Reliability</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">✅</span>
              </div>
              <div className="section-content">
                <p>At HEATGO, product reliability is paramount. All formulations undergo stringent thermal conductivity testing (ASTM D5470), thermal stability evaluation (TGA/DSC), dielectric breakdown testing (ASTM D149), and bleed resistance analysis to ensure uncompromised field performance.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.6 }}
          >
            <h3>Innovation-Driven, Application-Focused</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">💡</span>
              </div>
              <div className="section-content">
                <p>Our R&D team continuously evolves our material technologies to align with emerging thermal management challenges in next-generation electronics and power devices. Customization and technical support are available for specific end-use requirements, ensuring our customers receive optimized, application-ready thermal management solutions.</p>
                <p>With increasing electronic device miniaturization and power density, effective heat management is critical. We provide advanced thermal pastes, heat sink compounds, and nano-enhanced heat dissipation products that optimize performance, enhance safety, and extend equipment life.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.7 }}
          >
            <h3>Applications</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">📱</span>
              </div>
              <div className="section-content">
                <ul>
                  <li>Heat sink compounds for LED lighting systems</li>
                  <li>Thermal pastes for home appliances and electric fans</li>
                  <li>EV battery module thermal management</li>
                  <li>Solar energy thermal management</li>
                  <li>Consumer electronics and control panels</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="cta-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
          >
            <h3>Ready to Optimize Your Thermal Management?</h3>
            <p>Contact our technical team to discover how HEATGO® solutions can enhance the performance and reliability of your electronic applications.</p>
            <button className="cta-button">Request Technical Consultation</button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HeatDissipation; 